import { DataSource } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { ListSalesInvoicesFilters, SalesInvoice, SalesInvoiceService, SalesInvoiceStatus } from '@galaxy/billing';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { getRequestPageSize } from '../shared/datasource-helpers';

export interface SalesInvoiceListDataSourceLoadingContext {
  loading: boolean;
  empty: boolean;
}

export interface SalesInvoiceListDatasourceFilters extends ListSalesInvoicesFilters {
  searchTerm: string;
}

function updateStatusForSalesInvoice(id: string, status: SalesInvoiceStatus, sis: SalesInvoice[]): SalesInvoice[] {
  return sis.map((i) => {
    if (i && i.invoiceId === id) {
      i.status = status;
    }
    return i;
  });
}

/**
 * Data source for the SalesInvoiceList view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
@Injectable()
export class SalesInvoiceListDataSource extends DataSource<SalesInvoice> {
  private loading$$: BehaviorSubject<boolean>;
  private empty$$: BehaviorSubject<boolean>;

  public storedSalesInvoices$$: BehaviorSubject<SalesInvoice[]>;
  private displayedSalesInvoices$$: BehaviorSubject<SalesInvoice[]>;
  private cursor$$: BehaviorSubject<string>;
  private hasMore$$: BehaviorSubject<boolean>;

  constructor(private salesInvoiceService: SalesInvoiceService) {
    super();
    this.empty$$ = new BehaviorSubject<boolean>(true);
    this.displayedSalesInvoices$$ = new BehaviorSubject<SalesInvoice[]>([]);
    this.storedSalesInvoices$$ = new BehaviorSubject<SalesInvoice[]>([]);
    this.loading$$ = new BehaviorSubject<boolean>(true);
    this.hasMore$$ = new BehaviorSubject<boolean>(false);
    this.cursor$$ = new BehaviorSubject<string>('');
  }

  get hasMore$(): Observable<boolean> {
    return this.hasMore$$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  get empty$(): Observable<boolean> {
    return this.empty$$.asObservable();
  }

  get loadingContext$(): Observable<SalesInvoiceListDataSourceLoadingContext> {
    return combineLatest([this.loading$$, this.empty$$]).pipe(
      map(([loading, empty]) => {
        return { loading, empty };
      }),
    );
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<SalesInvoice[]> {
    return this.displayedSalesInvoices$$.asObservable();
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect(): void {
    this.loading$$.complete();
    this.storedSalesInvoices$$.complete();
    this.displayedSalesInvoices$$.complete();
    this.cursor$$.complete();
    this.hasMore$$.complete();
  }

  public reload(filters: SalesInvoiceListDatasourceFilters, pageSize: number, pageIndex: number): void {
    this.cursor$$.next('');
    this.listSalesInvoices(filters, '', pageSize)
      .pipe(take(1))
      .subscribe((newSalesInvoices) => {
        this.empty$$.next(!newSalesInvoices.length);
        this.storedSalesInvoices$$.next(newSalesInvoices);
        this.setPagedData(newSalesInvoices, pageIndex, pageSize);
      });
  }

  public updatePage(filters: SalesInvoiceListDatasourceFilters, pageSize: number, pageIndex: number): void {
    const storedItems = this.storedSalesInvoices$$.getValue();
    const requestPageSize = getRequestPageSize(storedItems.length, pageIndex, pageSize);
    if (this.hasMore$$.getValue() && requestPageSize > 0) {
      this.loadSalesInvoices(filters, pageSize, pageIndex);
    } else {
      this.setPagedData(storedItems, pageIndex, pageSize);
    }
  }

  public loadSalesInvoices(filters: SalesInvoiceListDatasourceFilters, pageSize: number, pageIndex: number): void {
    this.listSalesInvoices(filters, this.cursor$$.getValue(), pageSize)
      .pipe(
        map((newSalesInvoices: SalesInvoice[]) => [...this.storedSalesInvoices$$.value, ...newSalesInvoices]),
        take(1),
      )
      .subscribe((newSalesInvoices) => {
        this.empty$$.next(!newSalesInvoices.length);
        this.storedSalesInvoices$$.next(newSalesInvoices);
        this.setPagedData(newSalesInvoices, pageIndex, pageSize);
      });
  }

  private setPagedData(salesInvoices: SalesInvoice[], pageIndex: number, pageSize: number): void {
    // return the whole page, or whatever is left of our purchase list
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const endIndexForExisting = Math.min(salesInvoices.length, endIndex);
    this.displayedSalesInvoices$$.next(salesInvoices.slice(startIndex, endIndexForExisting));
  }

  private listSalesInvoices(
    filters: SalesInvoiceListDatasourceFilters,
    cursor: string,
    pageSize: number,
  ): Observable<SalesInvoice[]> {
    this.loading$$.next(true);
    return this.salesInvoiceService.listSalesInvoices(filters, cursor, pageSize, filters.searchTerm).pipe(
      tap((r) => {
        this.hasMore$$.next(r.hasMore);
        this.cursor$$.next(r.nextCursor);
      }),
      map((r) => r.results),
      finalize(() => this.loading$$.next(false)),
    );
  }

  public updateSalesInvoiceStatus(id: string, status: SalesInvoiceStatus): void {
    this.displayedSalesInvoices$$.next(
      updateStatusForSalesInvoice(id, status, this.displayedSalesInvoices$$.getValue()),
    );
    this.storedSalesInvoices$$.next(updateStatusForSalesInvoice(id, status, this.storedSalesInvoices$$.getValue()));
  }
}
